// Dashboard enum SSOT.
//
// Every dashboard enum is declared exactly once here as an `as const`
// tuple, then wrapped by `Sym()` into a registered Zod codec. The tuple
// drives three things from one definition:
//
//   1. the runtime-validating Zod schema (`z.enum`),
//   2. the derived TypeScript union (`(typeof TUPLE)[number]`),
//   3. an `llmDoc` registry entry carrying the LLM-facing type label
//      (and, for value-formatting enums, per-value example docs).
//
// `<ExprInstructions>` reads the `llmDoc` registry to
// render `value (note) | value | …` hints for each enum-typed kwarg, so
// the LLM sees concrete examples without us hand-writing prose per op.
//
// Scope note: proportion-variant and gauge-threshold enums are
// intentionally absent — currently ships cartesian + kpi + table only.

import { z } from 'zod';

import { formatCurrency, formatPercent } from '#core/components/dashboard/number-format.ts';
import { SERIES_COLORS } from '#core/lib/formatters/colors.ts';

import type { EnumDomainDoc, EnumDomainResolver } from '../../doc/types.ts';

/**
 * Doc metadata attached to a dashboard enum codec via `llmDoc`.
 * `typeLabel` is the `a | b | c` rendering of the domain; optional
 * `typeNote` adds a one-line hint; `enumValueDocs` maps individual
 * members to a short example string (used for the formatting enums).
 */
export type EnumDoc = {
  readonly typeLabel: string;
  readonly typeNote?: string;
  readonly enumValueDocs?: Readonly<Record<string, string>>;
};

/**
 * Registry of LLM-facing documentation for dashboard enum codecs.
 * Parallels the engine's `opMeta`/`pipelineVerbs`/`aggMeta` registries
 * but keyed by enum schema rather than op schema. Read by the doc
 * walker; never consulted at evaluation time.
 */
export const llmDoc = z.registry<EnumDoc>();

/**
 * Declare an enum once: build the Zod codec from the tuple and register
 * its LLM doc. `typeLabel` defaults to the `a | b | c` join; callers
 * pass `enumValueDocs` / `typeNote` for the value-formatting enums.
 *
 * SSOT helper — every dashboard enum flows through here so the tuple,
 * the runtime codec, the TS union, and the doc entry can never drift.
 */
const Sym = <const T extends readonly [string, ...string[]]>(
  values: T,
  extra: Omit<EnumDoc, 'typeLabel'> & { readonly typeLabel?: string } = {},
): z.ZodEnum<{ readonly [KeyType in T[number]]: KeyType }> => {
  const { typeLabel, ...rest } = extra;
  return z.enum(values).register(llmDoc, { typeLabel: typeLabel ?? values.join(' | '), ...rest });
};

// Series / cartesian enums.

/**
 * Chart accent colours available to series and reference markers. An
 * explicit, ordered subset of the `MantineThemeColors` SSOT —
 * `satisfies readonly MantineColor[]` compile-checks every name against
 * the theme so an invalid color can never enter the s-expr grammar.
 */
export type Color = (typeof SERIES_COLORS)[number];
export const Color = Sym(SERIES_COLORS, {
  typeNote: 'Mantine/OpenColor palette names.',
});

/** Line/border stroke styles (series stroke, reference-line style). */
export const STROKES = ['solid', 'dashed', 'dotted'] as const;
export type Stroke = (typeof STROKES)[number];
export const Stroke = Sym(STROKES, {
  typeNote:
    "line style for a series stroke or a reference-line; use to visually distinguish overlapping lines or to mark a non-data line (e.g. a 'dashed' target/limit reference-line vs 'solid' data series)",
  enumValueDocs: {
    solid: 'continuous line — default for real data series',
    dashed: 'long dashes — conventional for target/limit/threshold reference-lines',
    dotted: 'fine dots — secondary annotations or low-emphasis lines',
  },
});

/** Which y-axis a series binds to. */
export const AXIS_SIDES = ['left', 'right'] as const;
export type AxisSide = (typeof AXIS_SIDES)[number];
export const AxisSide = Sym(AXIS_SIDES, {
  typeNote:
    "which y-axis a series binds to; put series with very different value ranges on opposite axes so neither flattens the other (e.g. a 0-1 ratio on 'right' while a 0-1e6 count stays on 'left'). Default 'left'; only introduce 'right' when ranges/units genuinely mismatch",
  enumValueDocs: {
    left: 'primary y-axis (default) — the main metric',
    right: 'secondary y-axis — for a series whose unit or magnitude differs from the left-axis series',
  },
});

/** Series render type. */
export const SERIES_TYPES = ['line', 'bar', 'area', 'scatter'] as const;
export type SeriesType = (typeof SERIES_TYPES)[number];
export const SeriesType = Sym(SERIES_TYPES, {
  typeNote:
    'how a series is drawn; pick by the data shape — trends over time vs discrete category comparison vs cumulative volume vs scatter',
  enumValueDocs: {
    line: 'connected line — continuous trends over time/numeric x (the default for timeseries)',
    bar: 'bars — comparing values across discrete categories, or counts; combine with `:stack` none|stacked|percent (default none) for composition',
    area: 'filled line — emphasises magnitude/volume under the curve; stack for part-to-whole over x',
    scatter:
      'unconnected points — correlation/distribution of paired x,y samples where connecting lines would mislead; add `:z (col …)` to size each point (bubble chart)',
  },
});

/** Line/area interpolation between adjacent points (line/area series). */
export const CURVES = ['linear', 'smooth', 'step'] as const;
export type Curve = (typeof CURVES)[number];
export const Curve = Sym(CURVES, {
  typeNote:
    "interpolation between adjacent points on a line/area series; 'linear' is the honest default for sampled data, 'smooth' only for aesthetically continuous trends (can imply data that isn't there), 'step' for values that hold until the next change (states, setpoints, discrete counts)",
  enumValueDocs: {
    linear: 'straight segments point-to-point — default; faithful to the samples',
    smooth:
      'curved (monotone) interpolation — visually smooth but invents between-point shape; avoid for sparse/irregular data',
    step: 'horizontal hold then vertical jump — for state/setpoint/discrete series that stay constant between changes',
  },
});

/** The semantic domain of a cartesian chart's x-axis. */
export const X_KINDS = ['time', 'category', 'numeric'] as const;
export type XKind = (typeof X_KINDS)[number];
export const XKind = Sym(X_KINDS, {
  typeNote:
    "the semantic domain of the chart's x-axis; pick the kind that matches the x column's data so axis ticks, ordering, and reference-line/area values are interpreted correctly. Series sharing one chart must share the same x kind",
  enumValueDocs: {
    time: 'x is a timestamp; the series `:x` data column holds epoch-ms numbers, ticks render as dates, and x-anchored reference-line/area values are ISO/date strings',
    category: 'x is discrete labels (strings); ordering follows row order, one tick per distinct label',
    numeric: 'x is a continuous number (not time); linear numeric axis',
  },
});
/**
 * X-axis spacing policy — discrete (equal-width slot per row, bars
 * align to ticks) vs continuous (proportional spacing on numeric/time
 * axes) vs auto (discrete iff any series is a bar).
 */
export const X_SPACINGS = ['continuous', 'discrete', 'auto'] as const;
export type XSpacing = (typeof X_SPACINGS)[number];
export const XSpacing = Sym(X_SPACINGS, {
  typeNote:
    'how the x-axis distributes values across the plot width; defaults to auto — discrete when any series is a bar (so ticks align to bars), continuous otherwise. Set explicitly to override (e.g., continuous for irregular time data even with bars)',
  enumValueDocs: {
    continuous: 'proportional spacing on the x domain; numeric/time gaps preserved (line/area look)',
    discrete: 'one equal-width slot per row; ticks land exactly on bars',
    auto: 'discrete when any series is a bar; continuous otherwise',
  },
});

/** Stacking mode for multi-series bar/area charts. */
export const STACKS = ['none', 'stacked', 'percent'] as const;
export type Stack = (typeof STACKS)[number];
export const Stack = Sym(STACKS, {
  typeNote:
    "stacking mode for multi-series bar/area charts. Stacking sums series at each x to show a total/composition, so it needs pre-pivoted WIDE data: one row per x with each stacked series as its own numeric column (pivot/group before charting). 'none' for independent overlaid series",
  enumValueDocs: {
    none: 'series drawn independently (overlaid/grouped) — default; use to compare series against each other',
    stacked: 'series summed cumulatively at each x — shows part-to-whole totals; requires wide pre-pivoted columns',
    percent: 'stacked then normalized to 100% per x — shows share/composition, hides absolute magnitude',
  },
});

/** Bar/area orientation. */
export const ORIENTATIONS = ['vertical', 'horizontal'] as const;
export type Orientation = (typeof ORIENTATIONS)[number];
export const Orientation = Sym(ORIENTATIONS, {
  typeNote:
    "bar/area orientation. Default 'vertical'. Switch to 'horizontal' when category labels are many or long (they read better down the y-axis than crammed/rotated under an x-axis) or for ranking-style bar charts",
  enumValueDocs: {
    vertical: 'categories along x, value grows upward — default; best for time or few short labels',
    horizontal: 'categories along y, bars grow rightward — use for many/long category labels or top-N rankings',
  },
});

/** Axis a reference-line / reference-area is anchored to. */
export const REF_AXES = ['x', 'y'] as const;
export type RefAxis = (typeof REF_AXES)[number];
export const RefAxis = Sym(REF_AXES, {
  typeNote:
    "the axis a reference-line/reference-area is anchored to. 'y' draws a horizontal line/band at a value (targets, limits, normal range) — the common case. 'x' draws a vertical line/band at an x position; on a time x-axis pass the value as an ISO/date string (mark an event/window)",
  enumValueDocs: {
    x: "vertical marker at an x position; value is in x-axis units — on a 'time' x-axis an ISO/date string, on a numeric x-axis a number (event/time-window)",
    y: 'horizontal marker at a y value — thresholds, targets, limits, or a shaded normal-operating band',
  },
});

// Donut enums.

/** Slice value-label mode for a `(donut …)` tile. */
export const DONUT_LABELS = ['percent', 'value', 'none'] as const;
export type DonutLabels = (typeof DONUT_LABELS)[number];
export const DonutLabels = Sym(DONUT_LABELS, {
  typeNote: 'what each donut slice label shows; default percent',
  enumValueDocs: {
    percent: 'each slice labelled with its share of the total (e.g. 38%) — default',
    value: 'each slice labelled with its raw value, formatted via `:format`',
    none: 'no per-slice labels (rely on the legend / center readout)',
  },
});

// Metric (kpi) enums.

/**
 * Metric tile variant. Currently ships `kpi` only — `gauge` (and its
 * `threshold` children) are deferred, so this is a single-member enum
 * today. Kept as an enum (not a literal) so adding `gauge` later is a
 * one-tuple edit with no call-site churn.
 */
export const METRIC_VARIANTS = ['kpi'] as const;
export type MetricVariant = (typeof METRIC_VARIANTS)[number];
export const MetricVariant = Sym(METRIC_VARIANTS);

// Value-formatting SSOT — single vocabulary for "how is a value
// rendered" across every dashboard panel kind (table columns + KPI
// metric tiles). One place to add a format kind; consumers project
// the subset that applies.

/**
 * Canonical SSOT for value-rendering across dashboard panels. Used
 * directly by a table `(column …)` (where every kind is meaningful);
 * the numeric subset is projected as {@link METRIC_FORMATS} for a
 * `(metric …)` tile.
 */
export const DISPLAY_FORMATS = [
  'text',
  'number',
  'integer',
  'compact',
  'percent',
  'currency',
  'datetime',
  'duration-ms',
  'sparkline',
  'boolean',
  'category',
] as const;
export type DisplayFormat = (typeof DISPLAY_FORMATS)[number];

/**
 * Numeric subset of {@link DISPLAY_FORMATS} valid for a metric tile.
 * A metric IS a single number, so non-numeric kinds (text, datetime,
 * sparkline, boolean, category) are nonsensical. The `satisfies`
 * clause enforces the strict-subset relationship at compile time —
 * adding a non-numeric kind here is a type error.
 */
export const METRIC_FORMATS = [
  'number',
  'integer',
  'compact',
  'percent',
  'currency',
] as const satisfies readonly DisplayFormat[];
export type MetricFormat = (typeof METRIC_FORMATS)[number];

/**
 * SSOT for the human-readable example each format renders to.
 * Both the full {@link DisplayFormat} codec and the {@link
 * MetricFormat} subset codec project their docs from this single
 * mapping — no risk of `number` and `decimal` drifting apart again.
 *
 * The `percent`/`currency` examples are derived from the same
 * {@link formatPercent}/{@link formatCurrency} renderers the panels
 * use, so the agent glossary can never contradict what's drawn.
 */
const FORMAT_DOCS: Record<DisplayFormat, string> = {
  text: 'raw string',
  number: '1,234.56',
  integer: '1,235',
  compact: '1.2k, 3.4M',
  percent: `${formatPercent(42)} — absolute percentage (e.g. 42 → 42%), not a 0-1 fraction`,
  currency: formatCurrency(1234),
  datetime: '2024-01-15 14:30',
  'duration-ms': '1m 23s',
  sparkline: 'inline mini-chart over a nested series cell',
  boolean: 'checkmark for truthy, blank otherwise',
  category: 'stable colored badge per distinct string value',
};
export const MetricFormat = Sym(METRIC_FORMATS, {
  typeNote: 'how the metric value is rendered (numeric subset of DisplayFormat)',
  enumValueDocs: Object.fromEntries(METRIC_FORMATS.map((f) => [f, FORMAT_DOCS[f]])) as Record<MetricFormat, string>,
});
export const DisplayFormat = Sym(DISPLAY_FORMATS, {
  typeNote: 'how a value is rendered — full vocabulary; metric tiles use the numeric subset MetricFormat',
  enumValueDocs: FORMAT_DOCS,
});

// Table enums.

/** Horizontal alignment of a table column's cells. */
export const ALIGNS = ['left', 'center', 'right'] as const;
export type Align = (typeof ALIGNS)[number];
export const Align = Sym(ALIGNS);

/**
 * Canonical (name, codec, members) list of every dashboard enum.
 * SSOT for: the parametrized codec tests, and the `<ExprInstructions>`
 * enum-domain glossary. Adding an enum is one row here.
 */
export const ENUM_CODECS: readonly {
  readonly name: string;
  readonly codec: z.ZodEnum;
  readonly values: readonly string[];
}[] = [
  { name: 'Color', codec: Color, values: SERIES_COLORS },
  { name: 'Stroke', codec: Stroke, values: STROKES },
  { name: 'AxisSide', codec: AxisSide, values: AXIS_SIDES },
  { name: 'SeriesType', codec: SeriesType, values: SERIES_TYPES },
  { name: 'Curve', codec: Curve, values: CURVES },
  { name: 'XKind', codec: XKind, values: X_KINDS },
  { name: 'XSpacing', codec: XSpacing, values: X_SPACINGS },
  { name: 'Stack', codec: Stack, values: STACKS },
  { name: 'Orientation', codec: Orientation, values: ORIENTATIONS },
  { name: 'RefAxis', codec: RefAxis, values: REF_AXES },
  { name: 'DonutLabels', codec: DonutLabels, values: DONUT_LABELS },
  { name: 'MetricVariant', codec: MetricVariant, values: METRIC_VARIANTS },
  { name: 'MetricFormat', codec: MetricFormat, values: METRIC_FORMATS },
  { name: 'Align', codec: Align, values: ALIGNS },
  { name: 'DisplayFormat', codec: DisplayFormat, values: DISPLAY_FORMATS },
];

/**
 * Build the `<ExprInstructions enumDomains>` glossary from the
 * `llmDoc` registry. SSOT: pulls `typeLabel` / `enumValueDocs`
 * straight from each codec's registered doc — no hand-maintained
 * second copy of the value examples.
 */
export const dashboardEnumDomains = (): readonly EnumDomainDoc[] =>
  ENUM_CODECS.map(({ name, codec }) => {
    const doc = llmDoc.get(codec);
    return {
      name,
      typeLabel: doc?.typeLabel ?? '',
      ...(doc?.enumValueDocs === undefined ? {} : { valueDocs: doc.enumValueDocs }),
    };
  });
/**
 * Member-tuple → domain-name lookup, derived from the `ENUM_CODECS`
 * SSOT. Keyed by the joined member list rather than schema identity:
 * op kwargs declare a plain `z.enum(COLORS)` — a *fresh* schema object,
 * never the registered `Color` codec — so an identity lookup would
 * always miss. Member sets are distinct across every dashboard enum, so
 * the join is unambiguous.
 */
const DOMAIN_BY_MEMBERS: ReadonlyMap<string, string> = new Map(
  ENUM_CODECS.map(({ name, values }) => [values.join(' '), name] as const),
);

/**
 * Resolve an enum's ordered member list to its dashboard domain name
 * (e.g. `['blue','red',…]` → `Color`). Threaded into the doc
 * Zod-walker so enum-typed kwargs render as a `Color` reference; the
 * values themselves are expanded once in the "Enum domains" glossary.
 */
export const enumDomainNameOf: EnumDomainResolver = (members) => DOMAIN_BY_MEMBERS.get([...members].join(' '));
